import { useCallback, useEffect, useState } from 'react';

export interface Preset {
  name: string;
  createdAt: string;
  url: string;
}

const MAX_PRESETS = 20;
const key = (userId: string) => `presets:${userId}`;

function read(userId: string): Preset[] {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Preset[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(userId: string, presets: Preset[]): boolean {
  try {
    localStorage.setItem(key(userId), JSON.stringify(presets));
    return true;
  } catch {
    return false;
  }
}

function uniquify(name: string, existing: Preset[]): string {
  if (!existing.find((p) => p.name === name)) return name;
  let i = 2;
  while (existing.find((p) => p.name === `${name} (${i})`)) i++;
  return `${name} (${i})`;
}

export function useSavedPresets(userId: string): {
  presets: Preset[];
  save: (name: string, url: string) => { ok: boolean; reason?: string };
  remove: (index: number) => void;
} {
  const [presets, setPresets] = useState<Preset[]>(() => read(userId));

  // localStorage isn't a subscribable external store; we re-read only when the
  // user identity changes (rare in practice — usually once per signin).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPresets(read(userId));
  }, [userId]);

  const save = useCallback(
    (name: string, url: string): { ok: boolean; reason?: string } => {
      let result: { ok: boolean; reason?: string } = { ok: true };
      setPresets((current) => {
        if (current.length >= MAX_PRESETS) {
          result = { ok: false, reason: 'preset cap reached' };
          return current;
        }
        const finalName = uniquify(name, current);
        const next: Preset[] = [...current, { name: finalName, createdAt: new Date().toISOString(), url }];
        if (!write(userId, next)) {
          result = { ok: false, reason: 'storage full' };
          return current;
        }
        return next;
      });
      return result;
    },
    [userId],
  );

  const remove = useCallback(
    (index: number) => {
      setPresets((current) => {
        const next = current.filter((_, i) => i !== index);
        write(userId, next);
        return next;
      });
    },
    [userId],
  );

  return { presets, save, remove };
}
